import {
  startLoading,
  stopLoading,
} from '../../../common/modules/loading/actions'
import { LoadingTypes } from '../../../common/modules/loading/reducer'
import { isLoading } from '../../../common/utils/loadingState'
import { AnyAction, Dispatch } from 'redux'
import { camelize, snakeize } from 'casing'
import { updateAppAlert } from '../../../common/modules/appAlert/actions'
import { AxiosError, AxiosResponse } from 'axios'
import {
  fetchFlyoutAccountDetails,
  getUrlFromCloudAccount,
  patchAccount,
  postFlyoutAccountDetails,
  sendAzureData,
  sendAzurePartnerData,
  sendAzureSolutionsData,
  testTemplateEndpoint,
} from '../../../flyout/api/cmcm'
import {
  AccountUpdateSuccessMessage,
  FlyOutSettingsSuccessMessage,
  RequestFailureMessage,
} from '../../../common/utils/messagesContants'
import { RootState } from '../../../store'

import { ProductType } from '../../../common/modules/partner/reducer'
import { actionTypeWrapper } from '../../../common/utils/actionTypeWrapper'
import { Account } from '../../../common/modules/cmcm/types'
import { get, isEmpty, snakeCase } from 'common/utils/vanilla'
import {
  fetchAccounts,
  setAccounts,
} from '../../../common/modules/cmcm/accounts/actions'
import {
  awsTemplateNameCMCM,
  azureScopesUrls,
  formatSolutionTypeForUI,
  oppSyncProductPrd,
  temporaryArn,
} from '../../../common/utils/constants'
import { CloudType, cloudTypeToApiValue } from '../../../common/modules/types'
import { TestButtonStatus } from '@labrav/react-components/lib/@types/components/TestButtonComponent'
import { errorLogger } from '../../../common/utils/errorLogger'
import { getErrorMessages } from '../../../common/utils/error'
import {
  AzureAccountDetails,
  AzureConfigMetaDataResponse,
  AzureSolution,
  SetAccountOnboardingDataPayload,
  TemplateRows,
} from './reducer'
import {
  deactivateAzureAccount,
  fetchAzureAccountDetails,
  fetchAzureSolutionsData,
} from '../../api'
import { getItem, setItem } from '../../../common/utils/localStorage'
import {
  AccountData,
  PartnerType,
} from '../../../common/modules/partner/action'
import { patchPartnerOldEngineService } from '../../../common/api'
import {
  FlyOutListingData,
  FlyOutListingUserType,
} from '../../../admin/modules/flyOutSyncs/reducer'
import { patchSellersData } from '../../../flyout/api/markeplace'
import { AlertMessageType } from '../../../common/modules/appAlert/reducer'
import {
  MicrosoftCloudSettingsSections,
  MicrosoftCloudSettingsSectionsProps,
  MicrosoftCloudSettingsSteps,
  MicrosoftProductDetails,
} from '../../pages/components/MicrosoftCloudSettings/types'

export enum AccountsActions {
  SET_ACCOUNT_ONBOARDING_DATA = 'SET_ACCOUNT_ONBOARDING_DATA',
  SET_ACCOUNT_NAME = 'SET_ACCOUNT_NAME',
  SET_EXTERNAL_ACCOUNT_ID = 'SET_EXTERNAL_ACCOUNT_ID',
  SET_TEMPLATE_INPUT_FIELD = 'SET_TEMPLATE_INPUT_FIELD',
  SET_TEST_STATUS_ARN = 'SET_TEST_STATUS_ARN',
  SET_AZURE_DATA = 'SET_AZURE_DATA',
  UPDATE_VALUE_ON_ACCOUNT_DETAILS_AZURE = 'UPDATE_VALUE_ON_ACCOUNT_DETAILS_AZURE',
  SET_AZURE_ACCOUNT_DETAILS = 'SET_AZURE_ACCOUNT_DETAILS',
  UPDATE_VALUE_ON_ACCOUNT_DETAILS_AWS = 'UPDATE_VALUE_ON_ACCOUNT_DETAILS_AWS',
  SET_AZURE_ONBOARDING_COMPLETED = 'SET_AZURE_ONBOARDING_COMPLETED',
  // New actions for selected account (for form display, separate from onboarding validation)
  SET_SELECTED_ACCOUNT_DATA = 'SET_SELECTED_ACCOUNT_DATA',
  SET_SELECTED_AZURE_ACCOUNT_DETAILS = 'SET_SELECTED_AZURE_ACCOUNT_DETAILS',
  CLEAR_SELECTED_ACCOUNT_DATA = 'CLEAR_SELECTED_ACCOUNT_DATA',
}

type getSpecificFieldsBasedOnProductTypeProps = {
  partnerData: AccountData | null
  productType: ProductType
  flyoutSellerinfo?: FlyOutListingData
}

export const getSpecificFieldsBasedOnProductType = ({
  partnerData,
  productType,
  flyoutSellerinfo,
}: getSpecificFieldsBasedOnProductTypeProps) => {
  switch (productType) {
    case 'oppsync': {
      return {
        industries: snakeCase(partnerData?.industry || ''),
        partnershipTier: partnerData?.partnershipTier || '',
        partnershipType: partnerData?.partnershipType || '',
        terms: partnerData?.acceptedTermsAndConditions || false,
        isIsv: partnerData?.isIsv || false,
        isSrrp: partnerData?.isSrrp || false,
        accelerateProgram: partnerData?.accelerateProgram || false,
      }
    }
    case 'flyout': {
      return {
        isIsv: partnerData?.isIsv,
        origin: flyoutSellerinfo?.origin || '',
      }
    }
    case 'cloudfaas': {
      return {}
    }
    default: {
      return {}
    }
  }
}

interface GetInputFieldsArgs {
  productType: ProductType
  id: string
  accountFromAllAccounts?: Account
  flyoutSellerinfo?: FlyOutListingData
}

const getInputsFields = ({
  productType,
  accountFromAllAccounts,
  id,
  flyoutSellerinfo,
}: GetInputFieldsArgs) => {
  switch (productType) {
    case 'oppsync': {
      return [
        {
          keyName: 'iamRoleArn',
          value:
            accountFromAllAccounts?.accountDetails?.roles.rows.find(
              r => r.awsTemplateId === id
            )?.iamRoleArn || '',
          error: '',
          label: 'Enter Your ARN',
          showTestStatusButton: true,
          isRequired: true,
        },
      ]
    }
    case 'flyout': {
      return [
        {
          keyName: 'awsRoleArn',
          value: get(flyoutSellerinfo, 'sellerMetadata.awsRoleArn', ''),
          error: '',
          label: 'Enter Your ARN',
          showTestStatusButton: false,
          isRequired: true,
        },
        {
          keyName: 'awsCasS3BucketName',
          value: get(flyoutSellerinfo, 'sellerMetadata.awsCasS3BucketName', ''),
          error: '',
          label: 'Enter Your S3 CAS Bucket Name',
          showTestStatusButton: false,
          isRequired: true,
        },
        {
          keyName: 'awsCasSnsTopicArn',
          value: get(flyoutSellerinfo, 'sellerMetadata.awsCasSnsTopicArn', ''),
          error: '',
          label: 'Enter Your CAS SNS Topic ARN',
          showTestStatusButton: false,
          isRequired: true,
        },
      ]
    }
    case 'cloudfaas': {
      return []
    }
  }
}

export const getAccountRegistrationData =
  (
    cloudType: string,
    name: string,
    productId: string,
    partnerId: string,
    productType: ProductType,
    partnerType: PartnerType,
    accountFromAllAccounts?: Account,
    flyoutSellerinfo?: FlyOutListingData
  ) =>
  async (dispatch: Dispatch, getState: () => RootState) => {
    await dispatch(startLoading(LoadingTypes.TEMPLATES))
    const partnerData = getState().AccountData[partnerType].partnerData
    try {
      const { data } = await fetchFlyoutAccountDetails(
        name,
        cloudType,
        productId,
        partnerId
      )
      if (!isEmpty(data.rows)) {
        data.rows.forEach((row: any) => {
          row.inputsFields = getInputsFields({
            productType,
            accountFromAllAccounts,
            id: row.id,
            flyoutSellerinfo,
          })
        })
      }

      const newData = {
        ...camelize(data),
        organizationId: partnerId,
        productId: productId,
        name: accountFromAllAccounts?.accountDetails?.name || '',
        externalId:
          accountFromAllAccounts?.accountDetails?.externalAccountId || '',
        ...getSpecificFieldsBasedOnProductType({
          partnerData,
          productType,
          flyoutSellerinfo,
        }),
      }
      await dispatch(
        actionTypeWrapper(productType, setAccountRegistrationData(newData))
      )
    } catch (error) {
      dispatch(
        updateAppAlert({
          message: getErrorMessages([RequestFailureMessage])(
            (error as AxiosError).response as AxiosResponse<ErrorResponse>
          ),
          messageType: 'ERROR',
          autoClose: true,
        })
      )
      const globalState = getState()
      errorLogger({ globalState })(error as Error)
    } finally {
      await dispatch(stopLoading(LoadingTypes.TEMPLATES))
    }
  }
type SetAccountRegistrationDataProps = SetAccountOnboardingDataPayload & {
  rows: TemplateRows[]
  partnershipType?: string
}
export const setAccountRegistrationData = (
  data: SetAccountRegistrationDataProps
) => {
  const {
    cloudType,
    organizationId,
    rows,
    productId,
    name,
    externalId,
    industries,
    partnershipTier,
    partnershipType,
    terms,
    isIsv,
    isSrrp,
    accelerateProgram,
    origin,
  } = data
  return {
    type: AccountsActions.SET_ACCOUNT_ONBOARDING_DATA as AccountsActions.SET_ACCOUNT_ONBOARDING_DATA,
    payload: {
      cloudType,
      organizationId,
      roles: rows,
      productId,
      name,
      externalId,
      industries,
      partnershipTier,
      partnershipTypes: partnershipType,
      terms,
      isIsv,
      isSrrp,
      accelerateProgram,
      origin,
    },
  } as unknown as AnyAction
}

export const updateValueOnAccountDetails = (key: string, value: unknown) =>
  ({
    type: AccountsActions.UPDATE_VALUE_ON_ACCOUNT_DETAILS_AZURE as AccountsActions.UPDATE_VALUE_ON_ACCOUNT_DETAILS_AZURE,
    payload: {
      key,
      value,
    },
  }) as unknown as AnyAction
export const updateValueOnAccountDetailsForAWS = (
  key: string,
  value: unknown
) =>
  ({
    type: AccountsActions.UPDATE_VALUE_ON_ACCOUNT_DETAILS_AWS as AccountsActions.UPDATE_VALUE_ON_ACCOUNT_DETAILS_AWS,
    payload: {
      key,
      value,
    },
  }) as unknown as AnyAction

export const sendAccountRegistrationData =
  (productType: ProductType, partnerId: string, onboardingComplete?: boolean) =>
  async (dispatch: Dispatch, getState: () => RootState) => {
    await dispatch(startLoading(LoadingTypes.CMCM_FETCHING_ACCOUNTS))
    try {
      const state = getState()
      const data = state.accountForm[productType]
      const origin = data.accountDetails.AWS.origin
      const isFlyOut = productType === 'flyout'
      const shouldSendNameOrAccountId =
        productType === 'oppsync' ||
        (isFlyOut && origin !== FlyOutListingUserType.MANAGED)
      const name = shouldSendNameOrAccountId ? data.accountDetails.AWS.name : ''
      const externalAccountId = shouldSendNameOrAccountId
        ? data.accountDetails.AWS.externalAccountId
        : ''
      const roles = data.accountDetails.AWS.roles.rows.map(row => {
        const arn = get(row, 'inputsFields[0].value', '')
        return {
          templateId: row.id,
          iamRoleArn: isEmpty(arn) ? temporaryArn : arn,
        }
      })
      const accountId = (state.accounts[productType] || [])[0]?.id
      const newData = {
        ...data,
        accountDetails: {
          name,
          externalAccountId,
          type: 'ROOT',
          roles: { rows: roles },
        },
        name: `${productType}_prd`,
      }
      const isIsv = data.accountDetails.AWS.isIsv
      let dataToUpdatePartner: Record<string, unknown> = {
        isIsv: isIsv,
      }

      if (productType === 'oppsync') {
        dataToUpdatePartner = {
          ...dataToUpdatePartner,
          partnershipTier: data.accountDetails.AWS.partnershipTier,
          partnershipType: snakeCase(data.accountDetails.AWS.partnershipTypes),
          industry: data.accountDetails.AWS.industries,
          isSRRP: data.accountDetails.AWS.isSrrp,
          acceptedTermsAndConditions: data.accountDetails.AWS.terms,
          accelerateProgram: isIsv
            ? data.accountDetails.AWS.accelerateProgram
            : false,
        }
      }

      if (accountId) {
        if (shouldSendNameOrAccountId) {
          await patchAccount(
            accountId,
            snakeize({ accountDetails: newData.accountDetails })
          )
        }
      } else {
        await postFlyoutAccountDetails(snakeize(newData))
      }

      await dispatch(
        fetchAccounts(
          awsTemplateNameCMCM,
          newData.cloudType as CloudType,
          productType
        ) as never
      )
      await patchPartnerOldEngineService(
        partnerId,
        snakeize(dataToUpdatePartner)
      )
      const alert = { message: '', messageType: '' }
      if (isFlyOut) {
        const sellersMetaData: Record<string, string> = {}
        data.accountDetails.AWS.roles.rows[0]?.inputsFields.forEach(
          field => (sellersMetaData[field.keyName] = field.value)
        )
        const seller = {
          origin: data.accountDetails.AWS.origin,
          awsAccountId: externalAccountId,
          sellersMetaData,
        }
        await patchSellersData(
          partnerId,
          snakeize(seller),
          CloudType.AWS,
          'update_partner_details'
        )
        alert.messageType = 'SUCCESS'
        alert.message = FlyOutSettingsSuccessMessage
      } else {
        alert.messageType =
          productType === accountId && onboardingComplete
            ? 'SUCCESS'
            : 'WARNING'
        alert.message =
          accountId && onboardingComplete
            ? AccountUpdateSuccessMessage
            : `Your ${newData.cloudType} integration is in progress. Please contact your account manager.`
      }
      await dispatch(
        updateAppAlert({
          message: alert.message,
          messageType: alert.messageType as AlertMessageType,
          autoClose: !!(accountId && onboardingComplete),
        })
      )
    } catch (error) {
      dispatch(
        updateAppAlert({
          message: getErrorMessages([RequestFailureMessage])(
            (error as AxiosError).response as AxiosResponse<ErrorResponse>
          ),
          messageType: 'ERROR',
          autoClose: true,
        })
      )
      const globalState = getState()
      errorLogger({ globalState })(error as Error)
    } finally {
      await dispatch(stopLoading(LoadingTypes.CMCM_FETCHING_ACCOUNTS))
    }
  }

export const sendOppsyncAWSSettingsData =
  (partnerData: AccountData) =>
  async (dispatch: Dispatch, getState: () => RootState) => {
    try {
      const state = getState()
      const data = state.accountForm.oppsync
      const accountRoles = data.accountDetails.AWS.roles
      const accountId = (state.accounts.oppsync || [])[0]?.id
      const updateAccountDetails: Record<string, any> = {}

      const name = data.accountDetails.AWS.name || ''
      if (!isEmpty(name)) {
        updateAccountDetails['name'] = name
      }
      const externalAccountId = data.accountDetails.AWS.externalAccountId || ''
      if (!isEmpty(externalAccountId)) {
        updateAccountDetails['externalAccountId'] = externalAccountId
      }
      const roles: { templateId: string; iamRoleArn: string }[] = []
      data.accountDetails.AWS.roles.rows.forEach(row => {
        const arn = get(row, 'inputsFields[0].value', '')
        if (!isEmpty(arn) && row.inputsFields[0].testStatus === 'success') {
          roles.push({ templateId: row.id, iamRoleArn: arn })
        }
      })

      if (roles.length > 0) {
        updateAccountDetails['roles'] = { rows: roles }
      }

      if (accountId) {
        await patchAccount(
          accountId,
          snakeize({
            accountDetails: { ...updateAccountDetails, type: 'ROOT' },
          })
        )
      } else {
        const response = await postFlyoutAccountDetails(
          snakeize({
            cloudType: CloudType.AWS,
            name: 'oppsync_prd',
            organizationId: partnerData?.partnerId,
            productId: 'oppsync',
            accountDetails: { ...updateAccountDetails, type: 'ROOT' },
          })
        )
        const account = response.data

        await dispatch(
          actionTypeWrapper('oppsync', setAccounts(camelize([account])))
        )

        if (!isEmpty(accountRoles)) {
          accountRoles.rows.forEach((row: any) => {
            row.inputsFields = getInputsFields({
              productType: 'oppsync',
              accountFromAllAccounts: account,
              id: row.id,
            })
          })
        }

        await dispatch(
          actionTypeWrapper(
            'oppsync',
            setAccountRegistrationData({
              ...camelize(accountRoles),
              organizationId: partnerData.partnerId,
              productId: 'oppsync',
              name: account?.account_details?.name || '',
              externalId: account?.account_details?.external_account_id || '',
              ...getSpecificFieldsBasedOnProductType({
                partnerData,
                productType: 'oppsync',
              }),
            })
          )
        )
      }
    } catch (error) {
      dispatch(
        updateAppAlert({
          message: getErrorMessages([RequestFailureMessage])(
            (error as AxiosError).response as AxiosResponse<ErrorResponse>
          ),
          messageType: 'ERROR',
          autoClose: true,
        })
      )
      const globalState = getState()
      errorLogger({ globalState })(error as Error)
    }
  }

export const testTemplate =
  (
    templateId: string,
    cloudType: CloudType,
    productType: ProductType,
    partnerId: string,
    keyName: string
  ) =>
  async (dispatch: Dispatch, getState: () => RootState) => {
    await dispatch(
      actionTypeWrapper(productType, setStatus(templateId, 'loading', keyName))
    )

    const state = getState()
    const templateInputsFields = state.accountForm[
      productType
    ].accountDetails.AWS.roles.rows.find(r => r.id === templateId)?.inputsFields
    const iamRoleArn =
      templateInputsFields?.find(
        templateInputsField => templateInputsField.keyName === keyName
      )?.value || ''
    try {
      await testTemplateEndpoint(templateId, cloudType, iamRoleArn, partnerId)
      await dispatch(
        actionTypeWrapper(
          productType,
          setStatus(templateId, 'success', keyName)
        )
      )
    } catch (err) {
      await dispatch(
        actionTypeWrapper(
          productType,
          setStatus(
            templateId,
            'error',
            keyName,
            getErrorMessages([RequestFailureMessage])(
              (err as AxiosError).response as AxiosResponse<ErrorResponse>
            )
          )
        )
      )
      const error: Error = {
        name: 'productType',
        message: `Getting error in this product Type: ${productType} , with this templateId ${templateId}`,
      }
      const globalState = getState()
      errorLogger({ globalState })(error as Error)
    }
  }

export const getCloudAccountDataForAzure =
  (productType: ProductType, partnerId: string) =>
  async (dispatch: Dispatch, getState: () => RootState) => {
    await dispatch(startLoading(LoadingTypes.CMCM_FETCHING_ACCOUNTS))
    const state = getState()
    const partnerData = state.AccountData.user.partnerData
    try {
      let newData
      const value = await getItem('azureOauth')
      if (!value) {
        const { data } = await getUrlFromCloudAccount(partnerId)
        newData = {
          ...data,
          cloudType: partnerData?.activeCloudProvider,
          organizationId: partnerData?.partnerId,
          productId: productType.toUpperCase(),
        }
        await setItem('azureOauth', newData, { ttlInSeconds: 60 * 5 })
      }
      const finalData = value ? value : newData

      await dispatch(
        actionTypeWrapper(
          productType,
          setAzureAccountDetails(camelize(finalData))
        )
      )
    } catch (error) {
      dispatch(
        updateAppAlert({
          message: getErrorMessages([RequestFailureMessage])(
            (error as AxiosError).response as AxiosResponse<ErrorResponse>
          ),
          messageType: 'ERROR',
          autoClose: true,
        })
      )
      const globalState = getState()
      errorLogger({ globalState })(error as Error)
    } finally {
      await dispatch(stopLoading(LoadingTypes.CMCM_FETCHING_ACCOUNTS))
    }
  }

export const sendAzureAccountRegistration =
  (
    productType: ProductType,
    code: string,
    stateFromUrl: string,
    sessionState: string,
    partnerId: string
  ) =>
  async (dispatch: Dispatch, getState: () => RootState) => {
    await dispatch(startLoading(LoadingTypes.GENERAL))
    const state = getState()

    // CRITICAL: Check if we're updating a selected account (settings page scenario)
    // If selectedAccountId exists and matches partnerId, we're on a settings page
    // In this case, read from selectedAccountDetails instead of accountDetails
    const selectedAccountId = state.accountForm[productType].selectedAccountId
    const isSelectedAccount = selectedAccountId === partnerId

    // Get data from the correct source based on whether we're on a settings page
    const azureAccountDetails = isSelectedAccount
      ? state.accountForm[productType].selectedAccountDetails.AZURE
      : state.accountForm[productType].accountDetails.AZURE

    try {
      const dataToSend = {
        cloudType: cloudTypeToApiValue(CloudType.AZURE),
        productId: productType.toUpperCase(),
        name: oppSyncProductPrd,
        scopes: azureScopesUrls,
        accountDetails: {
          code,
          state: stateFromUrl,
          sessionState,
        },
      }
      const partnerDataToSend = {
        mpnId: azureAccountDetails.MPNID,
        companyName: azureAccountDetails.name,
        industry: azureAccountDetails.industries,
        partnerType: azureAccountDetails.microsoftPartnerType,
        isIsv: azureAccountDetails.ISV,
      }

      await sendAzureData(snakeize(dataToSend), partnerId)
      await sendAzurePartnerData(snakeize(partnerDataToSend), partnerId)

      await dispatch(
        updateAppAlert({
          message: 'Connection Established Successfully',
          messageType: 'SUCCESS',
          autoClose: true,
        })
      )
    } catch (error) {
      const e = error as AxiosError
      const errorResponse = e.response as AxiosResponse<ErrorResponse>
      if (errorResponse && errorResponse.status === 409) {
        // if returns 409, it means that the Azure account was already created together with its auth flow
        return
      }
      await dispatch(
        updateAppAlert({
          message: getErrorMessages([RequestFailureMessage])(
            (error as AxiosError).response as AxiosResponse<ErrorResponse>
          ),
          messageType: 'ERROR',
          autoClose: true,
        })
      )
      const globalState = getState()
      errorLogger({ globalState })(error as Error)
    } finally {
      await dispatch(stopLoading(LoadingTypes.GENERAL))
    }
  }

export const setAccountName = (name: string) =>
  ({
    type: AccountsActions.SET_ACCOUNT_NAME as AccountsActions.SET_ACCOUNT_NAME,
    payload: name,
  }) as unknown as AnyAction

export const setExternalAccountId = (externalAccountId: string) =>
  ({
    type: AccountsActions.SET_EXTERNAL_ACCOUNT_ID as AccountsActions.SET_EXTERNAL_ACCOUNT_ID,
    payload: externalAccountId,
  }) as unknown as AnyAction

export const setTemplateInputFields = (
  templateId: string,
  keyName: string,
  value: string
) => ({
  type: AccountsActions.SET_TEMPLATE_INPUT_FIELD as AccountsActions.SET_TEMPLATE_INPUT_FIELD,
  payload: {
    templateId,
    keyName,
    value,
  },
})
export const setStatus = (
  templateId: string,
  status: TestButtonStatus,
  keyName: string,
  error?: string
) => ({
  type: AccountsActions.SET_TEST_STATUS_ARN as AccountsActions.SET_TEST_STATUS_ARN,
  payload: {
    templateId,
    status,
    keyName,
    error,
  },
})

export const setAzureAccountDetails = (
  accountDetails: AzureAccountDetails & {
    cloudType?: string
    productId?: string
    organizationId?: string
    solutions?: AzureSolution[]
  }
) => ({
  type: AccountsActions.SET_AZURE_ACCOUNT_DETAILS,
  payload: accountDetails,
})

export const getAzureAccountDetails =
  (partnerId: string, product: ProductType, isSettingsPage = false) =>
  async (dispatch: Dispatch, getState: () => RootState) => {
    // On settings pages, check if we already have the data for this exact partnerId
    if (isSettingsPage) {
      const selectedAccountId =
        getState().accountForm[product].selectedAccountId
      const selectedAccountDetails =
        getState().accountForm[product].selectedAccountDetails.AZURE

      // Skip if we already have the data for this exact partnerId
      if (
        selectedAccountId === partnerId &&
        selectedAccountDetails?.fetchedForPartner === partnerId
      ) {
        if (
          isLoading(getState().loading)(
            LoadingTypes.AZURE_FETCHING_ACCOUNT_DETAILS
          )
        ) {
          // Already loading this partnerId, skip
          return
        }
        // We already have the data for this partnerId and we're not loading, skip
        return
      }

      // If partnerId is different, clear selected data first
      if (selectedAccountId && selectedAccountId !== partnerId) {
        await dispatch(clearSelectedAccountData('AZURE'))
      }
    } else {
      // On onboarding pages, check if we already have the data
      const currentAccountDetails =
        getState().accountForm[product].accountDetails.AZURE
      if (currentAccountDetails?.fetchedForPartner === partnerId) {
        if (
          isLoading(getState().loading)(
            LoadingTypes.AZURE_FETCHING_ACCOUNT_DETAILS
          )
        ) {
          // Already loading this partnerId, skip
          return
        }
        // We already have the data for this partnerId and we're not loading, skip
        return
      }
    }

    dispatch(startLoading(LoadingTypes.AZURE_FETCHING_ACCOUNT_DETAILS))
    try {
      // CRITICAL: Use partnerId parameter (which should be accountId from URL params on settings pages)
      const azureAccountDetails = await fetchAzureAccountDetails(partnerId)
      const solutions = await fetchAzureSolutionsData(partnerId)

      // CRITICAL: Fetch OAuth data (authUri, state) if missing, similar to onboarding
      // This ensures settings pages have all the same data as onboarding pages
      let oauthData: { authUri?: string; state?: string } = {}
      const currentAccountData = isSettingsPage
        ? getState().accountForm[product].selectedAccountDetails.AZURE
        : getState().accountForm[product].accountDetails.AZURE

      // Check if we need to fetch OAuth data
      if (!currentAccountData?.authUri || !currentAccountData?.state) {
        try {
          const value = (await getItem('azureOauth')) as {
            auth_uri?: string
            authUri?: string
            state?: string
          } | null
          if (value) {
            // Handle both snake_case and camelCase from cache
            oauthData = {
              authUri: value.auth_uri || value.authUri,
              state: value.state,
            }
          } else {
            // Fetch fresh OAuth data if not in cache
            const { data } = await getUrlFromCloudAccount(partnerId)
            // API returns snake_case, but camelize will be applied later
            // Handle both formats just in case
            const rawOauthData = camelize(data) as {
              authUri?: string
              state?: string
            }
            oauthData = {
              authUri:
                rawOauthData.authUri ||
                (data as { auth_uri?: string }).auth_uri,
              state: rawOauthData.state || (data as { state?: string }).state,
            }
            // Cache it for future use (similar to onboarding) - cache in snake_case format
            await setItem(
              'azureOauth',
              {
                auth_uri: oauthData.authUri,
                state: oauthData.state,
              },
              { ttlInSeconds: 60 * 5 }
            )
          }
        } catch (oauthError) {
          // If OAuth fetch fails, continue without it (might not be set up yet)
          console.warn('Failed to fetch OAuth data for Azure:', oauthError)
        }
      } else {
        // Use existing OAuth data
        oauthData = {
          authUri: currentAccountData.authUri,
          state: currentAccountData.state,
        }
      }

      // Determine if connection is active and complete based on account status and required fields
      // Use status from partner API instead of cloud account service
      const accountStatus = azureAccountDetails.status || 'draft'
      const isConnectionActive =
        accountStatus === 'active' &&
        azureAccountDetails.MPNID &&
        azureAccountDetails.microsoftPartnerType?.length > 0

      // Get existing steps from backend response
      const existingSteps = azureAccountDetails.azureConfigMetadata?.steps ?? {
        prerequisites: false,
        mpc_account_details: false,
        microsoft_product_details: false,
        link_mpc_account: false,
      }

      const accountDetailsPayload = {
        ...azureAccountDetails,
        ...oauthData, // Include authUri and state if fetched
        id: azureAccountDetails.id || partnerId, // Use partner ID if account ID not available
        status: accountStatus,
        organizationId: partnerId,
        fetchedForPartner: partnerId,
        terms: true,
        grantConsent: true,
        azureSolutionsData: solutions,
        azureConfigMetadata: {
          steps: {
            ...existingSteps,
            // Update link_mpc_account step based on connection status
            link_mpc_account: isConnectionActive
              ? true
              : existingSteps.link_mpc_account,
          },
          is_azure_onboarding_completed:
            azureAccountDetails.azureConfigMetadata
              ?.is_azure_onboarding_completed ?? false,
        },
      }

      // CRITICAL: On settings pages, ONLY set selectedAccountDetails (for form display)
      // NEVER set accountDetails (primary/default) on settings pages to avoid affecting onboarding validation
      // On onboarding pages, ONLY set accountDetails (primary) - never selectedAccountDetails
      if (isSettingsPage) {
        // Settings page: Update only selectedAccountDetails
        await dispatch(
          actionTypeWrapper<ProductType>(
            product,
            setSelectedAzureAccountDetails(partnerId, accountDetailsPayload)
          )
        )
      } else {
        // Onboarding page: Update only primary accountDetails
        dispatch(
          actionTypeWrapper<ProductType>(
            product,
            setAzureAccountDetails(accountDetailsPayload)
          )
        )
      }
    } catch (error) {
      const e = error as AxiosError
      const errorResponse = e.response as AxiosResponse<ErrorResponse>
      if (errorResponse && errorResponse.status === 404) {
        // if the partner is not found, that just means that the customer has not saved the data yet
        // not an error to alert for.
        return
      }
      dispatch(
        updateAppAlert({
          message: getErrorMessages([RequestFailureMessage])(errorResponse),
          messageType: 'ERROR',
          autoClose: false,
        })
      )
      errorLogger({ globalState: getState() })(error as Error)
    } finally {
      dispatch(stopLoading(LoadingTypes.AZURE_FETCHING_ACCOUNT_DETAILS))
    }
  }

export const disconnectAzureAccount =
  (
    partnerId: string,
    product: ProductType,
    accountId: string,
    isSettingsPage = false
  ) =>
  async (dispatch: Dispatch, getState: () => RootState) => {
    dispatch(startLoading(LoadingTypes.AZURE_DISCONNECTING_ACCOUNT))
    const state = getState()

    // CRITICAL: On settings pages, update selectedAccountDetails
    // On onboarding pages, update accountDetails
    const azureAccountDetails = isSettingsPage
      ? state.accountForm[product].selectedAccountDetails.AZURE
      : state.accountForm[product].accountDetails.AZURE

    try {
      const payload = { productId: product }
      await deactivateAzureAccount(partnerId, accountId, snakeize(payload))

      const updatedAccountDetails = {
        ...azureAccountDetails,
        status: 'deactivated' as const,
      }

      if (isSettingsPage) {
        // Settings page: Update only selectedAccountDetails
        await dispatch(
          actionTypeWrapper<ProductType>(
            product,
            setSelectedAzureAccountDetails(partnerId, updatedAccountDetails)
          )
        )
      } else {
        // Onboarding page: Update only primary accountDetails
        await dispatch(
          actionTypeWrapper<ProductType>(
            product,
            setAzureAccountDetails(updatedAccountDetails)
          )
        )
      }

      await dispatch(
        updateAppAlert({
          message: 'Disconnected account successfully',
          messageType: 'SUCCESS',
          autoClose: true,
        })
      )
    } catch (error) {
      const e = error as AxiosError
      const errorResponse = e.response as AxiosResponse<ErrorResponse>
      if (errorResponse && errorResponse.status === 404) {
        // if the partner is not found, that just means that the customer has not saved the data yet
        // not an error to alert for.
        return
      }
      dispatch(
        updateAppAlert({
          message: getErrorMessages([RequestFailureMessage])(errorResponse),
          messageType: 'ERROR',
          autoClose: false,
        })
      )
      errorLogger({ globalState: getState() })(error as Error)
    } finally {
      dispatch(stopLoading(LoadingTypes.AZURE_DISCONNECTING_ACCOUNT))
    }
  }
const mapFormikValuesToPartnerData = (
  formikValues: MicrosoftCloudSettingsSectionsProps,
  isAzureOnboardingCompleted?: boolean
) => ({
  mpnId:
    formikValues[MicrosoftCloudSettingsSections.MPC_ACCOUNT_DETAILS]?.mpnID,
  companyName: '',
  industry: '',
  partnerType:
    formikValues[MicrosoftCloudSettingsSections.MPC_ACCOUNT_DETAILS]
      ?.partnerType,
  isIsv:
    formikValues[MicrosoftCloudSettingsSections.MPC_ACCOUNT_DETAILS]?.isISV,
  hasAccessToMpcAccountAsAdminUser:
    formikValues[MicrosoftCloudSettingsSections.PREREQUISITES]
      ?.hasMPCAccessAsAdmin,
  separateUserForLabraIntegrationCreatedInMpc:
    formikValues[MicrosoftCloudSettingsSections.PREREQUISITES]
      ?.hasSeparateUserForLabraIntegration,
  reseller:
    formikValues[MicrosoftCloudSettingsSections.MPC_ACCOUNT_DETAILS]
      ?.isConsultingPartner,
  solutionMustBeConfiguredWithMpc:
    formikValues[MicrosoftCloudSettingsSections.PREREQUISITES]
      ?.hasConfiguredSolutionWithMPC,
  microsoftAccountName:
    formikValues[MicrosoftCloudSettingsSections.MPC_ACCOUNT_DETAILS]
      ?.accountName,
  azureConfigMetadata: {
    steps: {
      prerequisites:
        formikValues[MicrosoftCloudSettingsSections.STEPS]?.prerequisites,
      mpcAccountDetails:
        formikValues[MicrosoftCloudSettingsSections.STEPS]?.mpcAccountDetails,
      microsoftProductDetails:
        formikValues[MicrosoftCloudSettingsSections.STEPS]
          ?.microsoftProductDetails,
      linkMpcAccount:
        formikValues[MicrosoftCloudSettingsSections.STEPS]?.linkMpcAccount,
    },
    isAzureOnboardingCompleted: isAzureOnboardingCompleted ?? false,
  },
  status: formikValues[MicrosoftCloudSettingsSections.LINK_MPC_ACCOUNT]
    ?.isMPCConnected
    ? 'active'
    : 'deactivated',
})

export const sendMicrosoftCloudSettingsData =
  (partnerId: string, formikValues: MicrosoftCloudSettingsSectionsProps) =>
  async (dispatch: Dispatch, getState: () => RootState) => {
    await dispatch(startLoading(LoadingTypes.SEND_MICROSOFT_CLOUDSETTINGS_DATA))
    try {
      const state = getState()
      const productType = 'oppsync'

      // CRITICAL: Check if we're updating a selected account (settings page scenario)
      // If selectedAccountId exists and matches partnerId, we're on a settings page
      // In this case, ONLY update selectedAccountDetails, NEVER update the default accountDetails
      const selectedAccountId = state.accountForm[productType].selectedAccountId
      const isSelectedAccount = selectedAccountId === partnerId

      // If we have a selectedAccountId but it doesn't match, we're on a different settings page
      // Don't update anything in this case (shouldn't happen, but safety check)
      if (selectedAccountId && selectedAccountId !== partnerId) {
        console.warn(
          'sendMicrosoftCloudSettingsData: partnerId does not match selectedAccountId, skipping update',
          {
            partnerId,
            selectedAccountId,
          }
        )
        return
      }

      // Get onboarding completion status from the correct data source
      const isAzureOnboardingCompleted = isSelectedAccount
        ? (state.accountForm[productType].selectedAccountDetails.AZURE
            .azureConfigMetadata?.is_azure_onboarding_completed ?? false)
        : (state.accountForm[productType].accountDetails.AZURE
            .azureConfigMetadata?.is_azure_onboarding_completed ?? false)

      const partnerDataToSend = mapFormikValuesToPartnerData(
        formikValues,
        isAzureOnboardingCompleted
      )
      await sendAzurePartnerData(snakeize(partnerDataToSend), partnerId)

      // CRITICAL: On settings pages (selected account), ONLY update selectedAccountDetails
      // NEVER update accountDetails (default/primary) to avoid affecting onboarding validation
      if (isSelectedAccount) {
        const currentSelectedData =
          state.accountForm[productType].selectedAccountDetails.AZURE
        const mappedData = mapFormikValuesToPartnerData(
          formikValues,
          isAzureOnboardingCompleted
        )
        const updatedData = {
          ...(currentSelectedData || {}),
          ...mappedData,
          fetchedForPartner: partnerId, // Ensure partnerId is preserved
          microsoftAccountName:
            mappedData.microsoftAccountName === null
              ? undefined
              : mappedData.microsoftAccountName,
          reseller:
            mappedData.reseller === null ? undefined : mappedData.reseller,
          ISV: mappedData.isIsv ?? false, // ISV is required boolean, map from isIsv
          azureConfigMetadata: mappedData.azureConfigMetadata
            ? {
                steps: {
                  prerequisites:
                    mappedData.azureConfigMetadata.steps.prerequisites,
                  mpc_account_details:
                    mappedData.azureConfigMetadata.steps.mpcAccountDetails,
                  microsoft_product_details:
                    mappedData.azureConfigMetadata.steps
                      .microsoftProductDetails,
                  link_mpc_account:
                    mappedData.azureConfigMetadata.steps.linkMpcAccount,
                },
                is_azure_onboarding_completed:
                  mappedData.azureConfigMetadata.isAzureOnboardingCompleted,
              }
            : undefined,
        }
        await dispatch(
          actionTypeWrapper<ProductType>(
            productType,
            setSelectedAzureAccountDetails(partnerId, updatedData)
          )
        )
      } else {
        // Only update default account details if we're NOT on a settings page (onboarding scenario)
        // This ensures onboarding validation uses the correct data
        const currentData = state.accountForm[productType].accountDetails.AZURE
        const mappedData = mapFormikValuesToPartnerData(
          formikValues,
          isAzureOnboardingCompleted
        )
        const updatedData = {
          ...(currentData || {}),
          ...mappedData,
          fetchedForPartner: partnerId,
          microsoftAccountName:
            mappedData.microsoftAccountName === null
              ? undefined
              : mappedData.microsoftAccountName,
          reseller:
            mappedData.reseller === null ? undefined : mappedData.reseller,
          ISV: mappedData.isIsv ?? false, // ISV is required boolean, map from isIsv
          azureConfigMetadata: mappedData.azureConfigMetadata
            ? {
                steps: {
                  prerequisites:
                    mappedData.azureConfigMetadata.steps.prerequisites,
                  mpc_account_details:
                    mappedData.azureConfigMetadata.steps.mpcAccountDetails,
                  microsoft_product_details:
                    mappedData.azureConfigMetadata.steps
                      .microsoftProductDetails,
                  link_mpc_account:
                    mappedData.azureConfigMetadata.steps.linkMpcAccount,
                },
                is_azure_onboarding_completed:
                  mappedData.azureConfigMetadata.isAzureOnboardingCompleted,
              }
            : undefined,
        }
        await dispatch(
          actionTypeWrapper<ProductType>(
            productType,
            setAzureAccountDetails(updatedData)
          )
        )
      }
    } catch (error) {
      const e = error as AxiosError
      const errorResponse = e.response as AxiosResponse<ErrorResponse>
      if (errorResponse && errorResponse.status === 409) {
        return
      }
      await dispatch(
        updateAppAlert({
          message: getErrorMessages([RequestFailureMessage])(
            (error as AxiosError).response as AxiosResponse<ErrorResponse>
          ),
          messageType: 'ERROR',
          autoClose: true,
        })
      )
      const globalState = getState()
      errorLogger({ globalState })(error as Error)
    } finally {
      await dispatch(
        stopLoading(LoadingTypes.SEND_MICROSOFT_CLOUDSETTINGS_DATA)
      )
    }
  }

export const sendMicrosoftSolutionsDetails =
  (
    partnerId: string,
    solutions: Array<{
      solution_name: string
      solution_id: string
      publisher_name: string
      solution_type: string
      id?: string
    }>
  ) =>
  async (dispatch: Dispatch, getState: () => RootState) => {
    await dispatch(startLoading(LoadingTypes.SEND_MICROSOFT_SOLUTIONS_DATA))

    try {
      await sendAzureSolutionsData(
        solutions as unknown as Record<string, unknown>,
        partnerId
      )
    } catch (error) {
      const e = error as AxiosError
      const errorResponse = e.response as AxiosResponse<ErrorResponse>

      if (errorResponse && errorResponse.status === 409) {
        return
      }

      await dispatch(
        updateAppAlert({
          message: getErrorMessages([RequestFailureMessage])(
            (error as AxiosError).response as AxiosResponse<ErrorResponse>
          ),
          messageType: 'ERROR',
          autoClose: true,
        })
      )

      const globalState = getState()
      errorLogger({ globalState })(error as Error)
    } finally {
      await dispatch(stopLoading(LoadingTypes.SEND_MICROSOFT_SOLUTIONS_DATA))
    }
  }

export const transformPrerequisitesFromGet = (data: AzureAccountDetails) => {
  return {
    hasMPCAccessAsAdmin: data.hasAccessToMpcAccountAsAdminUser ?? false,
    hasSeparateUserForLabraIntegration:
      data.separateUserForLabraIntegrationCreatedInMpc ?? false,
    hasConfiguredSolutionWithMPC: data.solutionMustBeConfiguredWithMpc ?? false,
  }
}
export const transformAccountDetailsFromGet = (data: AzureAccountDetails) => {
  return {
    partnerType: data.microsoftPartnerType ?? [],
    isISV: data.ISV ?? false,
    isConsultingPartner: data.reseller ?? false,
    mpnID: data.MPNID ?? '',
    accountName: data.microsoftAccountName ?? '',
  }
}
export const transformLinkMpcFromGet = (data: AzureAccountDetails) => {
  return {
    isMPCConnected: data.status === 'active' ? true : false,
  }
}
export const transformStepsFromGet = (
  data: AzureConfigMetaDataResponse
): MicrosoftCloudSettingsSteps => {
  return {
    prerequisites: data.steps.prerequisites ?? false,
    mpcAccountDetails: data.steps.mpc_account_details ?? false,
    microsoftProductDetails: data.steps.microsoft_product_details ?? false,
    linkMpcAccount: data.steps.link_mpc_account ?? false,
  }
}
export const transformSolutionsFromGet = (
  azureSolutionsData: Array<{
    solution_type: string
    solution_id: string
    solution_name: string
    publisher_name: string
    id?: string
  }>
): MicrosoftProductDetails => {
  return {
    productDetails: azureSolutionsData.map((solution, index) => ({
      id:
        solution.id ||
        `${index}-${solution.solution_name || Math.random().toString(36).substring(2, 10)}`,
      solutionId: solution.solution_id,
      solutionName: solution.solution_name,
      publisherName: solution.publisher_name,
      solutionType: formatSolutionTypeForUI(solution.solution_type),
    })),
  }
}
export const setAzureOnboardingCompleted = (value: boolean) => ({
  type: AccountsActions.SET_AZURE_ONBOARDING_COMPLETED,
  payload: {
    value,
  },
})

// Actions for managing selected account data (separate from onboarding validation)
export const setSelectedAccountData = (
  accountId: string,
  data: SetAccountRegistrationDataProps
) => {
  const {
    cloudType,
    organizationId,
    rows,
    productId,
    name,
    externalId,
    industries,
    partnershipTier,
    partnershipType,
    terms,
    isIsv,
    isSrrp,
    accelerateProgram,
    origin,
  } = data
  return {
    type: AccountsActions.SET_SELECTED_ACCOUNT_DATA as AccountsActions.SET_SELECTED_ACCOUNT_DATA,
    payload: {
      accountId,
      data: {
        cloudType,
        organizationId,
        roles: rows,
        productId,
        name,
        externalId,
        industries,
        partnershipTier,
        partnershipTypes: partnershipType,
        terms,
        isIsv,
        isSrrp,
        accelerateProgram,
        origin,
      },
    },
  } as unknown as AnyAction
}

export const setSelectedAzureAccountDetails = (
  accountId: string,
  accountDetails: AzureAccountDetails & {
    cloudType?: string
    productId?: string
    organizationId?: string
  }
) =>
  ({
    type: AccountsActions.SET_SELECTED_AZURE_ACCOUNT_DETAILS as AccountsActions.SET_SELECTED_AZURE_ACCOUNT_DETAILS,
    payload: {
      accountId,
      accountDetails,
    },
  }) as unknown as AnyAction

export const clearSelectedAccountData = (cloudType?: string) =>
  ({
    type: AccountsActions.CLEAR_SELECTED_ACCOUNT_DATA as AccountsActions.CLEAR_SELECTED_ACCOUNT_DATA,
    payload: cloudType ? { cloudType } : undefined,
  }) as unknown as AnyAction
